import { authApiClient } from './api';
import type {
  AuthScope,
  OtpChannel,
  OtpContactVerificationConfirmRequest,
  OtpContactVerificationRequest,
  OtpContactVerificationResponse,
  OtpEnrollmentRequest,
  OtpStatusResponse,
  UserRole
} from '../types';

export interface LoginRequest {
  email: string;
  password: string;
  scope?: AuthScope;
  requestedScopes?: string[];
  otpChallengeId?: string;
  otpCode?: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  confirmPassword: string;
  fullName: string;
  authScope: AuthScope;
  role?: string;
  // DPDP consent capture at signup (KAN-780). The backend rejects a registration unless both
  // acceptTerms and acceptPrivacy are true and records a consent-ledger row per accepted purpose.
  acceptTerms?: boolean;
  acceptPrivacy?: boolean;
  acceptMarketing?: boolean;
}

export interface AuthUserPayload {
  id: string;
  email: string;
  name: string;
  authScope: string;
  createdAt: string;
  scopes?: string[];
  firstName?: string;
  lastName?: string;
  role: UserRole;
  status?: 'ACTIVE' | 'BLOCKED' | 'PENDING';
  mfaEnabled?: boolean;
}

export interface AuthResponse {
  user: AuthUserPayload;
  accessToken?: string | null;
  token?: string | null; // Legacy support
  refreshToken?: string | null;
  scopes?: string[];
  tokenExpiresAt?: string;
  mfaRequired?: boolean;
  otpRequired?: boolean;
  otpChallengeId?: string | null;
  otpChannel?: OtpChannel | string | null;
  otpEnrolled?: boolean | null;
  userId?: string; // V2 API includes userId
}

export interface RefreshTokenRequest {
  refreshToken?: string;
}

export interface RefreshTokenResponse {
  token?: string | null;
  refreshToken?: string | null;
  tokenExpiresAt?: string | null;
  scopes?: string[];
  user?: AuthUserPayload;
  userId?: string;
}

export interface LogoutRequest {
  refreshToken?: string;
}

export interface MessageResponse {
  message: string;
}

export const authApi = {
  /**
   * Register a new user
   */
  register: async (data: RegisterRequest): Promise<AuthResponse> => {
    // Register does not require authentication header
    return authApiClient.post<AuthResponse>('/register', data, false);
  },

  /**
   * Login user
   */
  login: async (credentials: LoginRequest): Promise<AuthResponse> => {
    // Login does not require authentication header
    return authApiClient.post<AuthResponse>('/login', credentials, false);
  },

  /**
   * TODO(deprecation): to be deprecated soon in favour of the server-side session
   * bridge the shared BFF broker calls (KAN-2036 — the broker POSTs the SAME body to
   * the SAME `/v2/auth/oidc/dhruvanta` endpoint, but server-to-server, so the tokens
   * never enter the browser). Do not build new work on this method: reaching it
   * requires the caller to be holding live IdP tokens in the JS heap. Retained only
   * for the `VITE_DHRUVANTA_AUTH_MODE=legacy` rollback.
   *
   * Exchange a verified Dhruvanta IdP login for a retail session (KAN-668 / KAN-747). KAN-747:
   * the frontend obtains BOTH the app-scoped user access token (entitlement) and the id_token
   * (verified email) from the IdP's API-first login and posts both; the bridge sets the httpOnly
   * session cookies, so callers hydrate state via checkAuth().
   */
  signInWithDhruvanta: async (
    accessToken: string,
    idToken: string,
  ): Promise<AuthResponse> => {
    return authApiClient.post<AuthResponse>('/oidc/dhruvanta', { accessToken, idToken }, false);
  },

  /**
   * Refresh access token
   */
  refreshToken: async (data: RefreshTokenRequest = {}): Promise<RefreshTokenResponse> => {
    // Refresh token request does not need X-Portal-Key
    return authApiClient.post<RefreshTokenResponse>('/refresh', data, false);
  },

  /**
   * Get current user information
   */
  getCurrentUser: async (): Promise<AuthResponse['user']> => {
    // Auth service v2 profile endpoint: GET /api/v2/auth/users/profile.
    // This is the silent "am I logged in?" probe run on app boot (AuthContext.checkAuth) — a 401
    // here is the normal anonymous-visitor case, so it must NOT bounce the user to /login.
    return authApiClient.get<AuthResponse['user']>('/users/profile', true, {
      redirectOnAuthFailure: false,
    });
  },

  /**
   * Logout user
   */
  logout: async (data: LogoutRequest = {}): Promise<{ message: string }> => {
    // Logout requires auth token but not portal key
    return authApiClient.post<{ message: string }>('/logout', data, false);
  },

  /**
   * Start a password reset (KAN-782). Enumeration-safe: the backend ALWAYS returns the same
   * generic message whether or not the email maps to an account, so the UI must not infer
   * existence from the response. No auth required.
   */
  forgotPassword: async (email: string): Promise<MessageResponse> => {
    return authApiClient.post<MessageResponse>('/password/forgot', { email }, false);
  },

  /**
   * Complete a password reset with the token from the emailed link (KAN-782). The backend sets
   * the new password and revokes existing sessions. No auth required.
   */
  resetPassword: async (token: string, newPassword: string): Promise<MessageResponse> => {
    return authApiClient.post<MessageResponse>('/password/reset', { token, newPassword }, false);
  },

  /**
   * Confirm an email address with the token from the emailed link (KAN-782). No auth required.
   */
  verifyEmail: async (token: string): Promise<MessageResponse> => {
    return authApiClient.post<MessageResponse>('/verify-email', { token }, false);
  },

  /**
   * Enroll or update OTP preferences for the current user.
   */
  enrollOtp: async (data: OtpEnrollmentRequest): Promise<OtpStatusResponse> => {
    return authApiClient.post<OtpStatusResponse>('/otp/enroll', data, true);
  },

  /**
   * Initiate verification for an OTP contact method (email/SMS).
   */
  challengeOtpContact: async (
    data: OtpContactVerificationRequest
  ): Promise<OtpContactVerificationResponse> => {
    return authApiClient.post<OtpContactVerificationResponse>(
      '/otp/contact/challenge',
      data,
      true
    );
  },

  /**
   * Confirm verification for an OTP contact using a received code.
   */
  confirmOtpContact: async (
    data: OtpContactVerificationConfirmRequest
  ): Promise<OtpStatusResponse> => {
    return authApiClient.post<OtpStatusResponse>('/otp/contact/confirm', data, true);
  },

  /**
   * Retrieve OTP enrollment status for the current user.
   */
  getOtpStatus: async (): Promise<OtpStatusResponse> => {
    // Background OTP-status refresh (also fired post-login). Treat like the boot probe — a 401
    // shouldn't yank the user to /login out from under a public page.
    return authApiClient.get<OtpStatusResponse>('/otp/status', true, {
      redirectOnAuthFailure: false,
    });
  },
};
